import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useParams } from "react-router";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import BrandLogo from "@components/BrandLogo";
import OK from "@assets/ok.svg?react";
import DOMPurify from "dompurify";

export default function PickColor({
  setCurrentQuestion,
  currentQuestion,
  setAnswers,
  answers,
  sendAnswers,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);

  const XGA = width >= 1020;

  const [colorArray, setColorArray] = useState([180, 50, 50]);

  const Blueprint = gameQuestionsQuery.content[currentQuestion].Blueprint;

  const correctHsl = gameQuestionsQuery.content[currentQuestion].color;
  const threshold = gameQuestionsQuery.content[currentQuestion].colorThreshold;

  function checkHue() {
    return Math.abs(correctHsl[0] - colorArray[0]) < threshold[0];
  }

  function checkSaturation() {
    return Math.abs(correctHsl[1] - colorArray[1]) < threshold[1];
  }

  function checkLightness() {
    return Math.abs(correctHsl[2] - colorArray[2]) < threshold[2];
  }

  function setDeviceColor(colorArray) {
    document
      .querySelector(".blueprint")
      .style?.setProperty(
        "--device-color",
        `hsla(${colorArray[0]}, ${colorArray[1]}%, ${colorArray[2]}%, 1)`
      );
  }

  function handleChange(index, e) {
    let newArray = [...colorArray];
    newArray[index] = +e.target.value;

    setColorArray(newArray);

    setDeviceColor(newArray);
  }

  function handleNextButtonClick() {
    let newAnswers = [...answers];
    newAnswers.push({
      question_ident: gameQuestionsQuery.content[currentQuestion].ident,
      answer_ident: true,
    });

    setAnswers(newAnswers);

    setCurrentQuestion((prev) => ++prev);
    setColorArray([180, 50, 50]);
    setDeviceColor([180, 50, 50]);
  }

  function getOutputPosition(value, maxValue) {
    const newVal = (value / maxValue) * 100;
    return `calc(${newVal}% + (${16 - newVal * 0.32}px))`;
  }

  return (
    <section className={`game-page pick-color ${ident}`}>
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrapper">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar
              value={currentQuestion / gameQuestionsQuery.content.length}
            />
            <div className="game-question">
              <h2
                className="question"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion].question),
                }}
              />
            </div>

            <div className="range-inputs">
              <div className="hue">
                <output
                  className="color-preview"
                  style={{
                    left: `clamp(16px, ${getOutputPosition(
                      colorArray[0],
                      360
                    )}, calc(100% - 14px)`,
                  }}
                >
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      width="24"
                      height="24"
                      rx="2"
                      fill={`hsla(${colorArray[0]}, 100%, 50%, 1)`}
                    />
                    <path
                      d="M8.12946 24.5812C7.65669 23.9194 8.12982 23 8.94319 23H15.0568C15.8702 23 16.3433 23.9194 15.8705 24.5812C15.6828 24.844 15.3798 25 15.0568 25H8.94319C8.62024 25 8.31717 24.844 8.12946 24.5812Z"
                      fill={`hsla(${colorArray[0]}, 100%, 50%, 1)`}
                    />
                  </svg>
                  {checkHue() && (
                    <span>
                      <OK />
                    </span>
                  )}
                </output>

                <div
                  className="range"
                  style={{
                    background: `linear-gradient(to right, hsla(0, 100%, 50%, 1), hsla(10, 100%, 50%, 1), hsla(20, 100%, 50%, 1), hsla(30, 100%, 50%, 1), hsla(40, 100%, 50%, 1), hsla(50, 100%, 50%, 1), hsla(60, 100%, 50%, 1), hsla(70, 100%, 50%, 1), hsla(80, 100%, 50%, 1), hsla(90, 100%, 50%, 1), hsla(100, 100%, 50%, 1), hsla(110, 100%, 50%, 1), hsla(120, 100%, 50%, 1), hsla(130, 100%, 50%, 1), hsla(140, 100%, 50%, 1), hsla(150, 100%, 50%, 1), hsla(160, 100%, 50%, 1), hsla(170, 100%, 50%, 1), hsla(180, 100%, 50%, 1), hsla(190, 100%, 50%, 1), hsla(200, 100%, 50%, 1), hsla(210, 100%, 50%, 1), hsla(220, 100%, 50%, 1), hsla(230, 100%, 50%, 1), hsla(240, 100%, 50%, 1), hsla(250, 100%, 50%, 1), hsla(260, 100%, 50%, 1), hsla(270, 100%, 50%, 1), hsla(280, 100%, 50%, 1), hsla(290, 100%, 50%, 1), hsla(300, 100%, 50%, 1), hsla(310, 100%, 50%, 1), hsla(320, 100%, 50%, 1), hsla(330, 100%, 50%, 1), hsla(340, 100%, 50%, 1), hsla(350, 100%, 50%, 1), hsla(360, 100%, 50%, 1))`,
                  }}
                >
                  <input
                    type="range"
                    min={0}
                    max={360}
                    step={1}
                    value={colorArray[0]}
                    onChange={(e) => handleChange(0, e)}
                    id={"hue"}
                  ></input>
                </div>
              </div>

              <div className="saturation">
                <output
                  className="color-preview"
                  style={{
                    left: `clamp(16px, ${getOutputPosition(
                      colorArray[1],
                      100
                    )}, calc(100% - 14px)`,
                  }}
                >
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      width="24"
                      height="24"
                      rx="2"
                      fill={`hsla(${colorArray[0]}, ${colorArray[1]}%, 50%, 1)`}
                    />
                    <path
                      d="M8.12946 24.5812C7.65669 23.9194 8.12982 23 8.94319 23H15.0568C15.8702 23 16.3433 23.9194 15.8705 24.5812C15.6828 24.844 15.3798 25 15.0568 25H8.94319C8.62024 25 8.31717 24.844 8.12946 24.5812Z"
                      fill={`hsla(${colorArray[0]}, ${colorArray[1]}%, 50%, 1)`}
                    />
                  </svg>
                  {checkSaturation() && (
                    <span>
                      <OK />
                    </span>
                  )}
                </output>

                <div
                  className="range"
                  style={{
                    background: `linear-gradient(to right, hsla(${colorArray[0]}, 0%, 50%, 1),hsla(${colorArray[0]}, 20%, 50%, 1),hsla(${colorArray[0]}, 40%, 50%, 1),hsla(${colorArray[0]}, 60%, 50%, 1),hsla(${colorArray[0]}, 80%, 50%, 1),hsla(${colorArray[0]}, 100%, 50%, 1))`,
                  }}
                >
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={colorArray[1]}
                    onChange={(e) => handleChange(1, e)}
                    id={"saturation"}
                  />
                </div>
              </div>
              <div className="lightness">
                <output
                  className="color-preview"
                  style={{
                    left: `clamp(16px, ${getOutputPosition(
                      colorArray[2],
                      100
                    )}, calc(100% - 14px)`,
                  }}
                >
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      width="24"
                      height="24"
                      rx="2"
                      fill={`hsla(${colorArray[0]}, ${colorArray[1]}%, ${colorArray[2]}%, 1)`}
                    />
                    <path
                      d="M8.12946 24.5812C7.65669 23.9194 8.12982 23 8.94319 23H15.0568C15.8702 23 16.3433 23.9194 15.8705 24.5812C15.6828 24.844 15.3798 25 15.0568 25H8.94319C8.62024 25 8.31717 24.844 8.12946 24.5812Z"
                      fill={`hsla(${colorArray[0]}, ${colorArray[1]}%, ${colorArray[2]}%, 1)`}
                    />
                  </svg>
                  {checkLightness() && (
                    <span>
                      <OK />
                    </span>
                  )}
                </output>
                <div
                  className="range"
                  style={{
                    background: `linear-gradient(to right, hsla(${colorArray[0]}, ${colorArray[1]}%, 0%, 1),hsla(${colorArray[0]}, ${colorArray[1]}%, 20%, 1),hsla(${colorArray[0]}, ${colorArray[1]}%, 40%, 1),hsla(${colorArray[0]}, ${colorArray[1]}%, 60%, 1),hsla(${colorArray[0]}, ${colorArray[1]}%, 80%, 1),hsla(${colorArray[0]}, ${colorArray[1]}%, 100%, 1))`,
                  }}
                >
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={colorArray[2]}
                    onChange={(e) => handleChange(2, e)}
                    id={"lightness"}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="side-img">
            <BrandLogo brands={gameQuestionsQuery?.brands} />
            <div className="preview">
              <img
                alt="previewBg"
                className="previewBg"
                src={gameQuestionsQuery.content[currentQuestion].previewBg}
              />
              <div className="blueprint">
                <Blueprint />
              </div>
              <div className="device-img">
                <img
                  src={gameQuestionsQuery.content[currentQuestion].img}
                  alt=""
                  draggable={false}
                />
              </div>
            </div>
            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button
                    disabled={
                      !(checkHue() && checkSaturation() && checkLightness())
                    }
                    action={handleNextButtonClick}
                  >
                    {gameQuestionsQuery.content[currentQuestion]
                      .nextButtonCopy ?? "Nächste"}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>

      {!XGA && (
        <StickyButton
          disabled={!(checkHue() && checkSaturation() && checkLightness())}
          action={handleNextButtonClick}
        >
          {gameQuestionsQuery.content[currentQuestion].nextButtonCopy ??
            "Nächste"}
        </StickyButton>
      )}
    </section>
  );
}
